<script setup lang="ts">
/**
 * Floating "Send feedback" button (tutor-uox.2.a).
 *
 * Anchored bottom-right of the authenticated AppShell. Visible on every
 * page so feedback collection isn't gated on the student knowing where
 * to look. Clicking opens SendFeedbackModal as an overlay; the FAB
 * stays mounted and visible behind the backdrop so a quick re-click on
 * the backdrop's edge doesn't lose state.
 */
import { ref } from "vue";

import SendFeedbackModal from "./SendFeedbackModal.vue";

const open = ref(false);

function show(): void {
  open.value = true;
}

function hide(): void {
  open.value = false;
}
</script>

<template>
  <button
    type="button"
    class="feedback-fab"
    data-testid="open-feedback"
    aria-label="Send feedback"
    @click="show"
  >
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1Zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1Z"
        fill="currentColor"
      />
    </svg>
    <span>Feedback</span>
  </button>

  <SendFeedbackModal v-if="open" @close="hide" />
</template>

<style scoped>
.feedback-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 200;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid var(--border-subtle, #d8d2c2);
  background: var(--surface, #faf6ec);
  color: var(--text, #2b2b2b);
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition:
    transform 0.12s,
    box-shadow 0.12s,
    background 0.12s;
}

.feedback-fab:hover {
  background: var(--surface-2, #f5efe1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.feedback-fab:focus-visible {
  outline: 2px solid var(--accent, #2b2b2b);
  outline-offset: 2px;
}
</style>
